import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { Flex, Spinner } from '@chakra-ui/react';
import { AppShell } from '@/widgets/app-shell/AppShell';
import { ClientShell } from '@/widgets/client-shell/ClientShell';
import { LoginPage } from '@/pages/LoginPage';
import { useAuthStore } from '@/shared/lib/auth/store';
import { PermissionGate } from '@/shared/ui/PermissionGate';
import { applyManifestForPath } from '@/shared/lib/pwa/manifestLink';

/* Protected pages are code-split: each loads its own chunk on first
   navigation (preloaded on link hover via `defaultPreload: 'intent'`),
   so the initial bundle carries only the shell + login. The shell and
   login stay eager — they're needed for the very first paint. */

const DashboardPage = lazyRouteComponent(() => import('@/pages/DashboardPage'), 'DashboardPage');
const OrdersPage = lazyRouteComponent(() => import('@/pages/OrdersPage'), 'OrdersPage');
const OrderDetailsPage = lazyRouteComponent(
  () => import('@/pages/OrderDetailsPage'),
  'OrderDetailsPage',
);
const MapPage = lazyRouteComponent(() => import('@/pages/MapPage'), 'MapPage');
const OperatorsPage = lazyRouteComponent(() => import('@/pages/OperatorsPage'), 'OperatorsPage');
const ProvidersPage = lazyRouteComponent(() => import('@/pages/ProvidersPage'), 'ProvidersPage');
const ClientsPage = lazyRouteComponent(() => import('@/pages/ClientsPage'), 'ClientsPage');
const CatalogPage = lazyRouteComponent(() => import('@/pages/CatalogPage'), 'CatalogPage');
const ConsumersPage = lazyRouteComponent(() => import('@/pages/ConsumersPage'), 'ConsumersPage');
const ConsumerZonesPage = lazyRouteComponent(
  () => import('@/pages/ConsumerZonesPage'),
  'ConsumerZonesPage',
);
const PricingPage = lazyRouteComponent(() => import('@/pages/PricingPage'), 'PricingPage');
const PersonalCarSettingsPage = lazyRouteComponent(
  () => import('@/pages/PersonalCarSettingsPage'),
  'PersonalCarSettingsPage',
);
const ChatPage = lazyRouteComponent(() => import('@/pages/ChatPage'), 'ChatPage');
const RefillRequestsPage = lazyRouteComponent(
  () => import('@/pages/RefillRequestsPage'),
  'RefillRequestsPage',
);
const ReportsPage = lazyRouteComponent(() => import('@/pages/ReportsPage'), 'ReportsPage');
const ReportBuilderPage = lazyRouteComponent(
  () => import('@/pages/ReportBuilderPage'),
  'ReportBuilderPage',
);
const AdminsPage = lazyRouteComponent(() => import('@/pages/AdminsPage'), 'AdminsPage');
const RolesPage = lazyRouteComponent(() => import('@/pages/RolesPage'), 'RolesPage');
const ClientReportBuilderPage = lazyRouteComponent(
  () => import('@/pages/ClientReportBuilderPage'),
  'ClientReportBuilderPage',
);
const ClientRefuelsPage = lazyRouteComponent(
  () => import('@/pages/ClientRefuelsPage'),
  'ClientRefuelsPage',
);

function PendingFallback() {
  return (
    <Flex align="center" justify="center" p="10" color="fg.muted">
      <Spinner />
    </Flex>
  );
}

/* Code-based routes (no generated tree). All real pages live behind
   AppShell at /; /login is public and redirects authenticated users
   away from itself. Route-level redirects read the auth store
   directly — Zustand is sync, so this happens during navigation
   without a render flicker. */

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'authenticated') {
      throw redirect({ to: user?.role === 'PROVIDER' ? '/client' : '/' });
    }
  },
  component: LoginPage,
});

const protectedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_protected',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'anonymous') throw redirect({ to: '/login' });
    // A provider account belongs in the client cabinet, not the admin shell.
    if (user?.role === 'PROVIDER') throw redirect({ to: '/client' });
  },
  component: AppShell,
});

const clientRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_client',
  beforeLoad: () => {
    const { status, user } = useAuthStore.getState();
    if (status === 'anonymous') throw redirect({ to: '/login' });
    // The cabinet is provider-only; anyone else bounces to the admin app.
    if (user?.role !== 'PROVIDER') throw redirect({ to: '/' });
  },
  component: ClientShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/',
  component: DashboardPage,
});

const ordersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders',
  // ГЛ-2/ГЛ-3: a dashboard KPI card deep-links here with a state filter (single value or a
  // comma-separated active-states list — see ACTIVE_ORDER_STATES_PARAM in features/orders/api.ts).
  // `completedOn`: the Главная weekly-completed chart deep-links here with a day + state=COMPLETED.
  // `consumerId`: «Заправки» on the B2C consumers page deep-links here for one account.
  validateSearch: (
    search: Record<string, unknown>,
  ): { state?: string; completedOn?: string; consumerId?: string } => ({
    state: typeof search.state === 'string' ? search.state : undefined,
    completedOn: typeof search.completedOn === 'string' ? search.completedOn : undefined,
    consumerId: typeof search.consumerId === 'string' ? search.consumerId : undefined,
  }),
  component: () => (
    <PermissionGate permission="manage_orders">
      <OrdersPage />
    </PermissionGate>
  ),
});

const orderDetailsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/orders/$orderId',
  component: () => (
    <PermissionGate permission="manage_orders">
      <OrderDetailsPage />
    </PermissionGate>
  ),
});

const mapRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/map',
  // КАРТА-8/9: "zoom to this order" navigates here with the target order's id so OrdersMap can
  // sharply pan/zoom to its marker on mount. ВОД-3: same deep-link shape for a driver, from the
  // "Водители" page.
  validateSearch: (search: Record<string, unknown>): { orderId?: string; driverId?: string } => ({
    orderId: typeof search.orderId === 'string' ? search.orderId : undefined,
    driverId: typeof search.driverId === 'string' ? search.driverId : undefined,
  }),
  component: MapPage,
});

const operatorsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/operators',
  component: () => (
    <PermissionGate permission="manage_operators">
      <OperatorsPage />
    </PermissionGate>
  ),
});

const providersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/providers',
  component: () => (
    <PermissionGate permission="manage_providers">
      <ProvidersPage />
    </PermissionGate>
  ),
});

const clientsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/clients',
  component: () => (
    <PermissionGate permission="manage_clients">
      <ClientsPage />
    </PermissionGate>
  ),
});

const catalogRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/catalog',
  component: () => (
    <PermissionGate permission="manage_catalog">
      <CatalogPage />
    </PermissionGate>
  ),
});

// B2C consumer accounts (B11). `manage_consumers` is its own permission, deliberately NOT
// `manage_clients` — that one gates the B2B account-scope entity, an unrelated concept.
const consumersRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/consumers',
  component: () => (
    <PermissionGate permission="manage_consumers">
      <ConsumersPage />
    </PermissionGate>
  ),
});

// B2C city provisioning is catalog-class reference data — same permission as /catalog.
const consumerZonesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/consumer-zones',
  component: () => (
    <PermissionGate permission="manage_catalog">
      <ConsumerZonesPage />
    </PermissionGate>
  ),
});

const personalCarSettingsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/personal-car-settings',
  component: () => (
    <PermissionGate permission="manage_catalog">
      <PersonalCarSettingsPage />
    </PermissionGate>
  ),
});

const pricingRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/pricing',
  component: () => (
    <PermissionGate permission="manage_pricing">
      <PricingPage />
    </PermissionGate>
  ),
});

const chatRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/chat',
  component: () => (
    <PermissionGate permission="manage_chat">
      <ChatPage />
    </PermissionGate>
  ),
});

const refillRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/refill-requests',
  component: () => (
    <PermissionGate permission="manage_refill_requests">
      <RefillRequestsPage />
    </PermissionGate>
  ),
});

const reportsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports',
  component: () => (
    <PermissionGate permission="view_reports">
      <ReportsPage />
    </PermissionGate>
  ),
});

const reportBuilderRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/reports/builder',
  component: () => (
    <PermissionGate permission="view_reports">
      <ReportBuilderPage />
    </PermissionGate>
  ),
});

const adminsRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admins',
  component: () => (
    <PermissionGate permission="manage_admins">
      <AdminsPage />
    </PermissionGate>
  ),
});

const adminRolesRoute = createRoute({
  getParentRoute: () => protectedRoute,
  path: '/admin-roles',
  component: () => (
    <PermissionGate permission="manage_roles">
      <RolesPage />
    </PermissionGate>
  ),
});

/* Two destinations. `/client` is the refuel history (the account's own refuels, with the photos
   and clips of each) — restored after the July rewrite left the cabinet reports-only while the
   backend kept serving `/v1/client/orders` and its media routes to nobody. `/client/reports` is
   the read-only list of reports an admin assembled and assigned to this client; the client-side
   report BUILDER stays gone, which is what that rewrite was actually about. */
const clientCabinetRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: '/client',
  component: ClientRefuelsPage,
});

const clientReportsRoute = createRoute({
  getParentRoute: () => clientRoute,
  path: '/client/reports',
  component: ClientReportBuilderPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  protectedRoute.addChildren([
    dashboardRoute,
    ordersRoute,
    orderDetailsRoute,
    mapRoute,
    operatorsRoute,
    providersRoute,
    clientsRoute,
    catalogRoute,
    consumersRoute,
    consumerZonesRoute,
    personalCarSettingsRoute,
    pricingRoute,
    chatRoute,
    refillRoute,
    reportsRoute,
    reportBuilderRoute,
    adminsRoute,
    adminRolesRoute,
  ]),
  clientRoute.addChildren([clientCabinetRoute, clientReportsRoute]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPendingComponent: PendingFallback,
});

// `main.tsx` sets the manifest link once at cold load, from the URL the tab was opened with.
// A provider logging in on `/` and getting client-side redirected to `/client` never reloads, so
// without this the install prompt would still offer the admin manifest until the next hard load.
router.subscribe('onResolved', ({ toLocation }) => {
  applyManifestForPath(toLocation.pathname);
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
